<script setup>
import { ref, computed } from 'vue'
import { menuRoutesEnum } from '@/router'
import { getI18n } from '@/plugins/i18n'

import 'devextreme-vue/text-area'
import { DxTreeList } from 'devextreme-vue/tree-list'

import * as dataGrid from '@/composables/data-grid'

import { codeNameDisplayExpr } from '@/composables/label'

import { useClipboard } from '@/composables/clipboard'
import { useColumns } from '@/composables/sale/sale-document-row-grid'
import { useDialogTitle } from '@/composables/sale/sale-document-grid'
import { useDocumentRow } from '@/composables/document-row'
import { useProcessingRow } from '@/composables/processing-row'
import { useRoundingRow } from '@/composables/rounding-row'

import { useGarageStore } from '@/stores/garage.store'
import { usePortalStore } from '@/stores/portal.store'
import { useRegistryStore } from '@/stores/registry.store'
import { useSaleStore } from '@/stores/sale.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import { useProductService } from '@/services/product.service'

import ClipboardExportDialog from '@/components/shared/ClipboardExportDialog.vue'
import ChipStatus from '@/components/shared/ChipStatus.vue'
import CustomToolbarAddButtonTemplate from '@/components/shared/CustomToolbarAddButtonTemplate.vue'
import CustomToolbarDeleteButtonTemplate from '@/components/shared/CustomToolbarDeleteButtonTemplate.vue'
import DepositDropDownBox from '@/components/shared/DepositDropDownBox.vue'
import DocumentRowTypeDropDownBox from '@/components/shared/DocumentRowTypeDropDownBox.vue'
import DocumentRowTypeToolbar from '@/components/shared/DocumentRowTypeToolbar.vue'
import EditLockButtonsTemplate from '@/components/shared/EditLockButtonsTemplate.vue'
import InformationToolTip from '@/components/shared/InformationToolTip.vue'
import ProductDropDownBox from '@/components/shared/ProductDropDownBox.vue'
import ReferenceLink from '@/components/shared/ReferenceLink.vue'
import SupersessionProductDropDownBox from '@/components/shared/SupersessionProductDropDownBox.vue'
import TimeEquivalentTextBox from '@/components/shared/TimeEquivalentTextBox.vue'
import CatalogsRowIcon from '@/components/shared/CatalogsRowIcon.vue'
import ImportRowIcon from '@/components/shared/ImportRowIcon.vue'
import ProcessingDropDownBox from '@/components/shared/ProcessingDropDownBox.vue'
import ExpenseRoundingRow from '@/components/shared/ExpenseRoundingRow.vue'
import RoundingToolbarButtons from '@/components/shared/RoundingToolbarButtons.vue'

const props = defineProps({
  data: Object,
  entityLock: Object,
  readOnly: {
    type: Boolean,
    default: false
  },
  isLocked: {
    type: Function,
    default: () => false
  }
})

const emits = defineEmits(['row-saved', 'content-ready'])

const formInstance = ref(null)
const productDropDownRef = ref(null)
const discount = props.data?.discount

const saveStateId = `sale-document-row-grid`
const gridId = `${saveStateId}_${props.data?.id || 0}`

const i18n = getI18n()
const registryStore = useRegistryStore()
const webSiteStore = useWebSiteStore()
const garageStore = useGarageStore()
const portalStore = usePortalStore()
const saleStore = useSaleStore()
const productService = useProductService()
const clipboard = useClipboard()

const computedDataSource = computed(() => {
  const document = props.data?.id
  return dataGrid.useODataSource(
    saleStore.api.getAbsoluteUrl() + saleStore.api.documentRowsUrl,
    {
      customParamsFn: () => { return { $notop: true, document: document } },
      avoidDuplicateDatasourceIds: true
    })
})

const isReadOnly = computed(() => {
  return (!!props.data?.electronic_invoice_progress_status && !portalStore.electronicInvoiceEditableFamilies.includes(portalStore.getElectronicInvoiceFamilyByProgressStatus(props.data?.electronic_invoice_progress_status))) || props.readOnly
})
const canAddDocumentRow = computed(() => !isReadOnly.value && !props.data?.has_rounding_row && !props.data?.has_rounding_expense_row)

const includesVat = computed(() => (props.data?.includes_vat))

const { gridRef, refreshGridAsync, focusGrid, setCellValueByField, refreshEditFormLayout } = dataGrid.useGridRef()
const { documentRowType, documentRowProductPurchaseFamily, isRoundingExpenseRow, isEditableRoundingPercentage, openRoundingRow, getEditMainInfoColumns, onDocumentRowUpdated, onDocumentRowInitNewRow, onDocumentRowEditorPreparing, onDocumentRowEditorPreparingSetIsReadOnly, onDocumentRowTypeChanged, setDocumentRowProductPurchaseFamily, applyRoundingPercentageFromProduct, initializeRoundingRowData, getRoundingExpenseColumns, focusQuantityEditor } = useDocumentRow(gridRef)

const { showRoundingRowPopup, roundingRowType, roundingRowReadOnly, roundingRowData, addExpenseRow, closeRoundingRow, repaintAllRows, onRoundingRowSaved, bindViewRoundingButton } = useRoundingRow({ gridRef, refreshGridAsync, onSaved: () => emits('row-saved', props.data?.id) })
const editors = ref({})
const supplierBrand = ref(registryStore.supplierBrands.find(i => i.id === props.data?.supplier_brand_ref))
const selectedRows = ref([])
const currency = webSiteStore.currencies.find(i => i.id === props.data?.currency_ref)
const saleDocumentCustomer = ref(null)
const supersessionId = ref(null)
const keepDescription = ref(true)

const {
  isProcessingRow,
  processingItems,
  onProcessingSelectionChanged,
  documentRowTypeChipData,
  getProcessingEditColumns,
  hideSaveAndNewForProcessingRow,
  onProcessingEditingStart,
  onProcessingDragChange,
  exportRows,
  batchDeleteRowIds,
  processingTreeListOptions,
} = useProcessingRow({ gridRef, selectedRows, setCellValueByField, hasMechanics: false, hasSuggestedTime: false })

let defaultToolbar = null

const isToolbarDisabled = ref(true)
const productUnitOfMeasure = ref(null)

const extractReferenceData = (rowData) => {
  const documentRow = rowData.quote_row

  const data = [
    { key: i18n.t('common.documentRowType'), value: codeNameDisplayExpr(webSiteStore.getLocalizedDocumentRowTypes().find(i => i.id === documentRow.document_row_type)) },
    { key: i18n.t('common.description'), value: documentRow.description },
    { key: i18n.t('common.quantity'), value: documentRow.quantity },
    { key: i18n.t('common.price'), value: documentRow.price },
    { key: i18n.t('common.unitOfMeasure'), value: codeNameDisplayExpr(webSiteStore.getLocalizedUnitsOfMeasure().find(i => i.id === documentRow.unit_of_measure_ref)) },
    { key: i18n.t('common.isMovement'), value: documentRow.is_movement ? '☑' : '' },
  ]

  return data
}

const onInitializedCustom = (e) => {
  if (isReadOnly.value || (props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !dataGrid.isAdding.value) {
    const toolbarItems = e.component.option('toolbarItems') || []

    const updatedToolbarItems = toolbarItems.map((item, index) => {
      if (index === toolbarItems.length - 1) return item
      return {
        ...item,
        visible: false,
      }
    })
    e.component.option('toolbarItems', updatedToolbarItems)
  } else {
    props.entityLock?.readOnlyToolbarItems(e)
  }

  hideSaveAndNewForProcessingRow(e)
}

const rowButtons = props.entityLock.getEntityLockButtons({
  visible: {
    edit: (e) => !(props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !isReadOnly.value && !props.entityLock.isLocked(false, e.entityId),
    delete: (e) => !props.data?.has_sale_document && ((e.data?.is_rounding_row || e.data?.is_rounding_expense_row) || !isReadOnly.value) && !props.entityLock.isLocked(false, e.entityId) && (!(props.data?.has_rounding_row || props.data?.has_rounding_expense_row) || e.data?.is_rounding_row || e.data?.is_rounding_expense_row) && !(e.data?.is_rounding_expense_row && props.data?.has_rounding_row),
    view: (e) => isReadOnly.value || props.entityLock.isLocked(false, e.entityId) || props.data?.has_rounding_row || props.data?.has_rounding_expense_row
  }
})

bindViewRoundingButton(rowButtons)

const { defaultActionTitle } = dataGrid.useDefaultDialogTitle()

const dialogTitle = (i) => {
  let title = `${defaultActionTitle(i)} ${i18n.t('common.saleDocumentRow')}`
  if (i.id) {
    title += ` ${i.row_number}`
  }
  return `${title} | ${useDialogTitle().itemTitle(props.data)}`
}

const roundingRowTitle = computed(() => dialogTitle(roundingRowData.value ?? {}))

const getEditColumns = (documentRowType) => {
  if (isProcessingRow.value) {
    return getProcessingEditColumns()
  }

  const mainInfoDataFields = getEditMainInfoColumns(documentRowType, dataGrid)
  const isLaborRow = documentRowType === webSiteStore.documentRowTypesEnum.LABOR
  const hideTabs = [webSiteStore.documentRowTypesEnum.TEXT, webSiteStore.documentRowTypesEnum.FREE].includes(documentRowType)

  if (isLaborRow) {
    const timeEquivalentField = mainInfoDataFields.find(i => i.dataField === 'timeEquivalent')
    if (timeEquivalentField) {
      timeEquivalentField.colSpan = 6
    }
  }
  const showAdditionalInfo = documentRowType !== webSiteStore.documentRowTypesEnum.TEXT && !isRoundingExpenseRow.value && !(props.data?.has_rounding_expense_row && dataGrid.isAdding.value)

  return [
    {
      itemType: 'group',
      colSpan: 2,
      items: [
        {
          itemType: 'group',
          caption: i18n.t('common.mainInfo'),
          name: 'mdi-view-list',
          captionTemplate: 'row-type-caption-template',
          colCount: 12,
          items: mainInfoDataFields
        },
        ...((showAdditionalInfo && !openRoundingRow.value) ? [{
          itemType: 'group',
          caption: i18n.t('common.additionalInfo'),
          name: 'mdi-currency-usd-circle',
          captionTemplate: 'generic-caption-template',
          cssClass: 'my-form-wrapper',
          colCount: 4,
          items: [
              { dataField: 'cost', editorOptions: { readOnly: true, ...dataGrid.useCurrencyOptions({ currency: currency }) } },
              { dataField: 'cost_valorization' },
              { dataField: 'discount_category_ref', colSpan: 2, editorOptions: { readOnly: true } },
              { dataField: 'product_price', editorOptions: dataGrid.useCurrencyOptions({currency: currency, readOnly: true}) },
              { dataField: 'charge_percentage', editorType: 'dxNumberBox', editorOptions: dataGrid.usePercentageOptions()},
              { dataField: 'price', label: { text: i18n.t('common.unitPriceNoVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'price_vat', label: { text: i18n.t('common.unitPriceVat') }, editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, precision: registryStore.companyData.default_display_price_precision}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'discount', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'discount_2', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'discount_3', editorType: 'dxNumberBox', editorOptions: { ...dataGrid.usePercentageOptions(), ...dataGrid.useMaxDiscountValueOptions(documentRowType) } },
              { dataField: 'total_discount', editorOptions: {...dataGrid.usePercentageOptions(), readOnly: true } },
              { dataField: 'vat_type_ref', editorOptions: { showClearButton: true }, colSpan: 2 },
              { dataField: 'total_no_vat', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
              { dataField: 'total_vat', editorOptions: { ...dataGrid.useCurrencyOptions({currency: currency, readOnly: true}), ...dataGrid.usePriceMinValueOptions(documentRowType) } },
          ]
        }] : [])
      ]
    },
    {
      itemType: 'tabbed',
      colSpan: 3,
      name: 'tabbed',
      tabs: hideTabs ? [] : [
        ...((isRoundingExpenseRow.value || openRoundingRow.value) ? [...getRoundingExpenseColumns(documentRowType, currency, dataGrid)] : []),
        ...(isLaborRow ? [
          {
            title: i18n.t('common.times'),
            items: [
              {
                itemType: 'group',
                caption: i18n.t('common.times'),
                colCount: 2,
                items: [
                  { dataField: 'actual_duration', label: { text: i18n.t('common.timeClock') }, editorOptions: { min: 0 } },
                  { dataField: 'suggestedTime' },
                  { dataField: 'operationCode' },
                  { dataField: 'recognizedTime' },
                ]
              },
            ]
          },
          {
            title: i18n.t('common.statistics'),
            items: [{ itemType: 'group', caption: i18n.t('common.statistics') }]
          },
        ] : [
          {
            title: i18n.t('common.ties'),
            items: [
              {
                itemType: 'group',
                caption: i18n.t('common.ties'),
                items: [{ template: 'supersessionProductEditor', colSpan: 5 }]
              },
            ]
          },
          {
            title: i18n.t('common.campaigns'),
            items: [{itemType: 'group', caption: i18n.t('common.campaigns') }]
          },
          {
            title: i18n.t('common.operatorsStock'),
            items: [{ itemType: 'group', caption: i18n.t('common.operatorsStock') }]
          },
        ]),
      ]
    },
  ]
}

const laborPriceContext = {
  workType: () => props.data?.source_worksheet?.work_type,
  garageReasonRef: () => props.data?.garage_reason_ref ?? props.data?.source_worksheet?.garage_reason_ref,
  interventionTypeRef: () => props.data?.intervention_type_ref,
}

const columns = useColumns(supplierBrand, keepDescription, currency, documentRowType, saleDocumentCustomer, supersessionId, documentRowProductPurchaseFamily, includesVat, productUnitOfMeasure, laborPriceContext)

const editing = dataGrid.useDefaultEditing(dialogTitle, {
  colCount: 5,
  items: getEditColumns(documentRowType.value),
  onInitialized: (e) => { formInstance.value = e.component },
}, {
  allowAdding: canAddDocumentRow.value && !props.entityLock?.isLocked(),
  allowUpdating: (e) => !isReadOnly.value && !(props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !props.entityLock?.isLocked(),
  allowDeleting: (e) => !isReadOnly.value && !props.entityLock?.isLocked() && (!(props.data?.has_rounding_row || props.data?.has_rounding_expense_row) || e.data?.is_rounding_row || e.data?.is_rounding_expense_row),
  isSaveAndNew: true,
  onInitializedCustom,
  entityLock: props.entityLock,
})

editing.popup.onShown = () => { if (dataGrid.isAdding.value) productDropDownRef.value?.focus?.() }

const onInitNewRow = async(e) => {
  isProcessingRow.value = false
  supersessionId.value = productUnitOfMeasure.value = null
  e.data.document = props.data?.id

  e.data.vat_type_ref = props.data.vat_type_ref || registryStore.companyData.default_vat_type_ref
  e.data.vat_type_percentage = webSiteStore.vatTypes.find(v => v.id === e.data.vat_type_ref)?.percentage
  e.data.total_discount = (e.data.discount ?? 0) + (e.data.discount_2 ?? 0) + (e.data.discount_3 ?? 0)
  productService.updateTotalDiscountValue(e.data, e.data)
  saleDocumentCustomer.value = registryStore.customers.find(c => c.id === props.data.customer_id)

  const brand = registryStore.supplierBrands.find(i => i.id === props.data?.supplier_brand_ref) ??
   registryStore.supplierBrands.find(i => i.id === registryStore.companyData.default_supplier_brand_ref)

  supplierBrand.value = (dataGrid.isSaveAndNew.value ? supplierBrand.value : null) ?? brand

  onDocumentRowInitNewRow(e, supplierBrand, discount, isToolbarDisabled, props.data?.deposit)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onEditorPreparing = (e) => {
  onDocumentRowEditorPreparing(e, isToolbarDisabled, supplierBrand, includesVat)
  if (e.parentType === 'dataRow') {
    e.editorOptions.onInitialized = (arg) => { editors.value[e.dataField] = arg.component }
  }
  if (e.parentType === 'dataRow' && e.dataField === 'product_ref') {
    e.editorOptions.disabled = !!e.row.data?.id || (typeof e.row.data?.supplier_brand_ref !== 'number')
  }
  if (e.parentType === 'dataRow' && e.dataField === 'vat_type_ref') {
    e.editorOptions.readOnly = props.data.customer?.vat_type_ref
  }
  if (e.parentType === 'dataRow' && e.dataField === 'product_price') {
    e.editorOptions.readOnly = true
  }
  if (e.parentType === 'dataRow' && e.dataField === 'rounding_percentage') {
    e.editorOptions.readOnly = props.readOnly || !isEditableRoundingPercentage.value
  }

  onDocumentRowEditorPreparingSetIsReadOnly(e, isReadOnly.value || (props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !dataGrid.isAdding.value, editors)
}

const onDocumentRowTypeSelect = (rowType) => {
  documentRowType.value = rowType
  supplierBrand.value = null
  setCellValueByField('document_row_type', rowType)
  onDocumentRowTypeChanged(rowType, getEditColumns)
}

const onEditingStart = (e) => {
  onProcessingEditingStart(e)
  supersessionId.value = e.data?.product_ref || null
  e.data.total_discount = (e.data.discount ?? 0) + (e.data.discount_2 ?? 0) + (e.data.discount_3 ?? 0)
  saleDocumentCustomer.value = props.data.customer
  documentRowType.value = e.data.document_row_type
  setDocumentRowProductPurchaseFamily(e.data)
  productUnitOfMeasure.value = e.data?.unit_of_measure_ref || null

  if (isRoundingExpenseRow.value) {
    initializeRoundingRowData(props, e.data, true)
  }

  productService.updatePriceVatValue(e.data, e.data)
  productService.updateTotalDiscountValue(e.data, e.data)
  onDocumentRowTypeChanged(documentRowType.value, getEditColumns)
}

const onSupersessionSelected = () => {
  const tabPanel = formInstance.value?.getEditor('tabbed')
  const tiesTabIndex = tabPanel?.option('items')?.findIndex(tab => tab.title === i18n.t('common.ties'))
  if (tiesTabIndex >= 0) {
    tabPanel.option('selectedIndex', tiesTabIndex)
  }
}

const onToolbarPreparing = (e) => {
  e.toolbarOptions.items.unshift(
    { location: 'after', template: 'dropdownToolbarItem' },
    clipboard.clipboardExportToolbarWidget({ disabled: props.readOnly || selectedRows.value.length === 0 }),
    { location: 'after', template: 'roundingToolbarButtons' }
  )

  defaultToolbar = dataGrid.useDefaultToolbar(e, { allowBatchDelete: true })
}

const onSelectionChanged = (e) => {
  selectedRows.value = e.selectedRowsData
  clipboard.clipboardExportToolbarButton.value.option('disabled', exportRows.value.length === 0 || exportRows.value.some(i => i.is_rounding_row || i.is_rounding_expense_row))
  defaultToolbar?.batchDeleteToolbar?.option('disabled', isReadOnly.value || selectedRows.value.length === 0)
}

const onSaved = () => { emits('row-saved', props.data?.id) }

const saveRoundingRow = (rowData) => saleStore.api.post(saleStore.api.documentRowsUrl, {
  ...rowData,
  document: props.data?.id,
  deposit: props.data?.deposit,
  cost_valorization: garageStore.costValorizationsEnum.NORMAL,
})

const parentId = computed(() => props.data?.id)
const { onDragEnd } = dataGrid.useDefaultRowReordering(saleStore.api.reorderSaleDocumentRowsAsync, parentId, gridRef, () => props.readOnly || props.entityLock.isLocked(false))
const refreshGridWithFocus = () => refreshGridAsync().then(focusGrid & emits('row-saved', props.data?.id))

const onProductValueChanged = (e) => {
  if (!e.value) {
    keepDescription.value = false
    supplierBrand.value = null
  }

  setDocumentRowProductPurchaseFamily(e.value)
  refreshEditFormLayout(getEditColumns(documentRowType.value), props.data.customer?.vat_type_ref || registryStore.companyData.default_vat_type_ref)
  applyRoundingPercentageFromProduct(e.value, setCellValueByField)

  if (e.value) { focusQuantityEditor(editors) }
}

const onNewProductSelected = (e) => {
  const grid = gridRef.value?.instance
  if (!grid) return

  const editingRow = grid.getVisibleRows().find(row => row.isEditing)
  if (!editingRow) return

  const rowIndex = editingRow.rowIndex
  grid.cellValue(rowIndex, 'product_ref', e)
  supersessionId.value = e?.id || null
  setDocumentRowProductPurchaseFamily(e)
  refreshEditFormLayout(getEditColumns(documentRowType.value), props.data.customer?.vat_type_ref || registryStore.companyData.default_vat_type_ref)
  applyRoundingPercentageFromProduct(e, setCellValueByField)
}

const onRowRemoved = async(e) => {
  emits('row-saved', props.data?.id, e.data)
  await refreshGridAsync()
  repaintAllRows()
}

const onBatchDeleteDocumentRowsAsync = async(ids) => {
  await saleStore.api.batchDeleteSaleDocumentRowsAsync(ids)
  emits('row-saved', props.data?.id)
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  entityLockId: () => props.data?.id,
  saveStateId: saveStateId,
  mode: 'row',
  columns: columns,
  editing: editing,
  selection: dataGrid.useDefaultGridSelection(),
  onInitNewRow: onInitNewRow,
  onEditorPreparing: onEditorPreparing,
  onEditingStart: onEditingStart,
  onToolbarPreparing: onToolbarPreparing,
  onSelectionChanged: onSelectionChanged,
  onRowUpdated: onDocumentRowUpdated,
  onRowRemoved: onRowRemoved,
  onRowInserted: onDocumentRowUpdated,
  onSaved: onSaved,
  focusGrid: focusGrid,
  onContentReady: (e) => {
    emits('content-ready', e)
    e.component.option('editing.allowAdding', canAddDocumentRow.value)
    e.component.option('editing.allowDeleting', !isReadOnly.value && (!(props.data?.has_rounding_row || props.data?.has_rounding_expense_row)))
  },
  rowDragging: {
    allowReordering: true,
    allowDropInsideItem: true,
    onDragEnd: onDragEnd,
    onDragChange: onProcessingDragChange
  },
  disabledFocusOnContentReady: true,
  ...processingTreeListOptions,
  splittedGridDetail: true,
})

const exportGridColumns = columns.filter(i => i.dataField !== 'unit_of_measure' && i.dataField !== 'discount_category' && i.dataField !== 'total_no_vat' && i.dataField !== 'worksheet.row_number' && i.dataField !== 'intervention_type_ref')

</script>
<template>
  <div class="row-grid-ctn">
    <clipboard-export-dialog
      v-if="clipboard.clipboardExportDialogVisible.value"
      v-model:visible="clipboard.clipboardExportDialogVisible.value"
      grid-id="sale-document-row-export-grid"
      :selected-rows="exportRows"
      :columns="exportGridColumns"
      :document-type="props.data?.sale_document_type"
      :source-includes-vat="includesVat"
      :entity-lock="props.entityLock"
      :is-read-only-row="isReadOnly || props.data?.has_rounding_row || props.data?.has_rounding_expense_row"
      @updated="refreshGridWithFocus"
      @cancel="focusGrid"
    />
    <expense-rounding-row
      :show-dialog="showRoundingRowPopup"
      :entity-lock="props.entityLock"
      :rounding-type="roundingRowType"
      :read-only="roundingRowReadOnly"
      :row-data="roundingRowData"
      :title="roundingRowTitle"
      :data="props.data"
      :save-endpoint="saveRoundingRow"
      @on-save="onRoundingRowSaved"
      @on-cancel="closeRoundingRow"
    />
    <dx-tree-list
      ref="gridRef"
      :key="gridId"
      v-bind="gridOptions"
      :data-source="computedDataSource"
    >
      <template #dropdownToolbarItem>
        <div class="row no-wrap items-center">
          <catalogs-row-icon
            :document-type="props.data?.sale_document_type"
            :vehicle-id="props.data?.vehicle_id"
          />
          <import-row-icon
            :disabled="isReadOnly || props.data?.has_rounding_row || props.data?.has_rounding_expense_row || props.entityLock.isLocked(false, props.data?.id)"
            :document-type="props.data?.sale_document_type"
            :document-id="props.data?.id"
            :entity-lock="props.entityLock"
            :displayed-sources="[webSiteStore.importRowTypesEnum.CATALOG, webSiteStore.importRowTypesEnum.LRW, webSiteStore.importRowTypesEnum.CLIPBOARD]"
            @updated="refreshGridWithFocus"
          />
        </div>
      </template>
      <template #roundingToolbarButtons>
        <rounding-toolbar-buttons
          :master-entity-id="props.data?.id"
          :entity-lock="props.entityLock"
          :read-only="isReadOnly"
          :has-rounding-row="props.data?.has_rounding_row"
          :has-rounding-expense-row="props.data?.has_rounding_expense_row"
          @add-rounding-row="addExpenseRow(true)"
          @add-expense-percentage-row="addExpenseRow(false)"
        />
      </template>
      <template #generic-caption-template="{ data: cellInfo }">
        <div class="dx-form-group-caption-wrapper">
          <q-icon
            :name="cellInfo.name"
            class="dx-form-group-caption-icon"
          />
          <span class="dx-form-group-caption-text">
            {{ cellInfo.caption }}
          </span>
        </div>
      </template>
      <template #row-type-caption-template="{ data: cellInfo }">
        <document-row-type-toolbar
          :data="cellInfo"
          :disabled="isReadOnly || isToolbarDisabled || (props.data?.has_rounding_expense_row && dataGrid.isAdding.value)"
          :document-row-type="documentRowType"
          @value-changed="(e) => onDocumentRowTypeSelect(e.value)"
        />
      </template>
      <template #productEditor="{ data: cellInfo }">
        <product-drop-down-box
          ref="productDropDownRef"
          :brand="supplierBrand"
          :document-row-type="documentRowType"
          :disabled="isReadOnly || (props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !dataGrid.isAdding.value"
          :data="cellInfo"
          :options="{ hideSupersession: true }"
          @supersession-selected="onSupersessionSelected"
          @value-changed="onProductValueChanged"
        />
      </template>
      <template #depositEditor="{ data: cellInfo }">
        <deposit-drop-down-box
          :data="cellInfo"
          :disabled="isReadOnly || (props.data?.has_rounding_row || props.data?.has_rounding_expense_row) && !dataGrid.isAdding.value || documentRowType === webSiteStore.documentRowTypesEnum.TEXT"
        />
      </template>
      <template #processingType="{ data: cellInfo }">
        <div :class="{ 'text-grey-7 text-italic': cellInfo.data.processing_type === garageStore.processingTypesEnum.DETAIL }">
          {{ cellInfo.displayValue }}
        </div>
      </template>
      <template #processingEditor="{ data: cellInfo }">
        <processing-drop-down-box
          :data="cellInfo"
          :items="processingItems()"
          :disabled="props.readOnly || !dataGrid.isAdding.value"
          @value-changed="onProcessingSelectionChanged"
        />
      </template>
      <template #supersessionProductEditor>
        <supersession-product-drop-down-box
          :data="{ supersession_id: supersessionId }"
          @new-product-selected="onNewProductSelected"
        />
      </template>
      <template #quoteRowRef="{ data: cellInfo }">
        <reference-link
          v-if="!!cellInfo.data.quote_row"
          :data="{
            series: cellInfo.data.quote_row?.quote_series,
            number: cellInfo.data.quote_row?.quote_number_mnemonic,
            rowNumber: cellInfo.data.quote_row?.row_number,
          }"
          :text-fields="['prefix', 'series', 'number']"
          :query-fields="['series', 'number']"
          :target="menuRoutesEnum.QUOTES"
        >
          <information-tool-tip
            :data="extractReferenceData({ ...cellInfo.data, type: portalStore.documentTypesEnum.GARAGE_QUOTE })"
          />
        </reference-link>
      </template>
      <template
        #editButtonsTemplate="{ data: cellInfo }"
      >
        <edit-lock-buttons-template
          :entity-name="'SaleDocument'"
          :master-entity-id="props.data?.id"
          :cell-info="cellInfo"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :buttons="rowButtons"
        />
      </template>
      <template #customToolbarAddButtonTemplate>
        <custom-toolbar-add-button-template
          v-if="canAddDocumentRow"
          :master-entity-id="props.data?.id"
          :grid="gridRef"
          :entity-lock="props.entityLock"
        />
      </template>
      <template #customToolbarDeleteButtonTemplate>
        <custom-toolbar-delete-button-template
          :master-entity-id="props.data?.id"
          :selected-rows-id="batchDeleteRowIds"
          :grid="gridRef"
          :entity-lock="props.entityLock"
          :on-delete="onBatchDeleteDocumentRowsAsync"
          :disabled="selectedRows.length === 0 || props.readOnly || props.data?.has_rounding_expense_row || props.data?.has_rounding_row"
        />
      </template>
      <template #documentRowType="{ data: cellInfo }">
        <document-row-type-drop-down-box
          :data="cellInfo"
          :value="cellInfo?.data?.document_row_type"
          :disabled="true"
        />
      </template>
      <template #timeEquivalentEditor>
        <time-equivalent-text-box
          :selected-brand-id="editors['supplier_brand_ref'].option('value')"
          :selected-quantity="editors['quantity'].option('value')"
          :selected-uom-id="productUnitOfMeasure"
        />
      </template>
      <template #documentRowTypeCellTemplate="{ data: templateOptions }">
        <chip-status :data="documentRowTypeChipData(templateOptions.data)" />
      </template>
    </dx-tree-list>
  </div>
</template>

<style scoped lang="scss">
.dx-form-group-caption-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-icon {
  font-size: 1.2rem;
  line-height: 1;
}

.dx-form-group-caption-wrapper .dx-form-group-caption-text {
  line-height: 1.2;
}
</style>
